import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import type { ChatMessage } from '@/types/chat';
import { ActionRunner } from '@/lib/chat/action-runner';  // Fixed import

export const useMessageHandling = (
  messages: ChatMessage[],
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void,
  setIsLoading: (isLoading: boolean) => void,
  clearState: () => void
) => {
  const { toast } = useToast();

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      content: message,
      timestamp: new Date(),
      role: 'user'
    };
    
    setMessages(prev => [...prev, newMessage]);
    setIsLoading(true);
    
    try {
      console.log('Отправка сообщения в Edge Function...');
      const { data: stream, error } = await supabase.functions.invoke('chat', {
        body: { 
          messages: [...messages, newMessage].map(msg => ({
            role: msg.role,
            content: msg.content
          }))
        }
      });

      if (error) {
        console.error('Ошибка отправки сообщения:', error);
        if (error.message.includes('401')) {
          toast({
            title: "Ошибка аутентификации",
            description: "Проверьте настройки API ключа в Supabase.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Ошибка",
            description: "Не удалось отправить сообщение. Пожалуйста, попробуйте снова.",
            variant: "destructive"
          });
        }
        throw error;
      }

      if (!stream) {
        throw new Error('Не получен ответ от сервера');
      }

      const reader = new Response(stream).body?.getReader();
      if (!reader) {
        throw new Error('Не удалось создать reader для потока');
      }

      let accumulatedText = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices[0]?.delta?.content;
              
              if (content) {
                accumulatedText += content;
                setMessages(prev => {
                  const lastMessage = prev[prev.length - 1];
                  if (lastMessage?.role === 'assistant') {
                    return [
                      ...prev.slice(0, -1),
                      { ...lastMessage, content: accumulatedText }
                    ];
                  } else {
                    return [
                      ...prev,
                      {
                        id: crypto.randomUUID(),
                        content: accumulatedText,
                        timestamp: new Date(),
                        role: 'assistant'
                      }
                    ];
                  }
                });
              }
            } catch (e) {
              console.error('Ошибка парсинга SSE сообщения:', e);
            }
          }
        }
      }

    } catch (error) {
      console.error('Ошибка в handleSendMessage:', error);
    } finally {
      setIsLoading(false);
      clearState();
    }
  };

  return { handleSendMessage };
};
